import {
  FC,
  HTMLAttributes,
  InputHTMLAttributes,
  useEffect,
  useState,
} from "react";
import clsx from "classnames";
import { Icon } from "../Icon/Icon";
import { useStyles } from "./TextField.style";
import {
  Typography,
  TypographyColor,
  TypographyVariant,
} from "../Typography/Typography";

export interface ITextFieldProps {
  label?: string;
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  errorMessage?: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>;
  isShowClear?: boolean;
  maxLength?: number;
  isError?: boolean;
  onChangeValue?: (value: string) => void;
  endAdornment?: React.ReactNode;
  isTextArea?: boolean;
  placeholder?: string;
}

export const TextField: FC<
  ITextFieldProps & HTMLAttributes<HTMLDivElement>
> = ({
  label,
  fullWidth,
  startIcon,
  errorMessage,
  isShowClear,
  inputProps,
  maxLength,
  isError,
  onChangeValue,
  endAdornment,
  isTextArea,
  placeholder,
  ...otherProps
}) => {
  const classes = useStyles();
  const [value, setValue] = useState<string | number | readonly string[]>(
    inputProps?.value || "",
  );

  useEffect(() => {
    if (inputProps && inputProps.value !== undefined) {
      setValue(inputProps.value);
    }
  }, [inputProps?.value]);

  const onChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (
      maxLength &&
      value?.toString()?.length >= maxLength &&
      (event.nativeEvent as InputEvent).inputType !== "deleteContentBackward"
    )
      return;
    onChangeValue && onChangeValue(event.target.value);
    setValue(event.target.value);
  };

  const onClear = () => {
    setValue("");
    onChangeValue && onChangeValue("");
  };

  return (
    <div {...otherProps} className={clsx(classes.root, otherProps.className)}>
      {label && (
        <Typography
          variant={TypographyVariant.BodyB5Bold}
          color={TypographyColor.Text2}
          data-testid="label-input"
        >
          {label}
        </Typography>
      )}
      <div className={clsx(classes.content, !fullWidth && classes.fitContent)}>
        {startIcon && <div className={classes.startAdornment}>{startIcon}</div>}
        {!isTextArea ? (
          <input
            {...inputProps}
            className={clsx(
              classes.input,
              inputProps?.className,
              fullWidth && classes.fullWidth,
              startIcon && classes.hasStartIcon,
              isShowClear && classes.hasClearIcon,
              isError && classes.errorBorder,
              inputProps?.disabled && classes.textDisable,
            )}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            disabled={inputProps?.disabled}
          />
        ) : (
          <textarea
            {...inputProps}
            value={value}
            onChange={onChange}
            className={clsx(
              classes.input,
              classes.textarea,
              inputProps?.className,
              fullWidth && classes.fullWidth,
              isError && classes.errorBorder,
            )}
          />
        )}
        {isShowClear && value && (
          <Icon
            name="icon-bold-close-circle"
            className={classes.closeIcon}
            onClick={onClear}
          />
        )}
        {endAdornment && (
          <div className={classes.endAdornment}>{endAdornment}</div>
        )}
      </div>
      {errorMessage && (
        <Typography
          variant={TypographyVariant.BodyB5Regular}
          color={isError ? TypographyColor.TextError : TypographyColor.Text1}
          data-testid="error-text"
        >
          <Icon
            name="icon-bold-danger"
            className={clsx(classes.dangerIcon, isError && "error")}
          />
          {errorMessage}
        </Typography>
      )}
    </div>
  );
};
